// src/Pages/Purchase/PurchaseData/AddPurchaseOrders.jsx
import React, { useState, useEffect } from "react";
import { usePurchaseOrders } from "../../../context/PurchaseOrdersContext";
import { useNavigate, Link } from "react-router-dom";
import "../../../Pages/Purchase/PurchaseData/AddPurchaseOrders.css";
import { API_URL } from "../../../config";

const AddPurchaseOrders = () => {
  const { addPurchaseOrder } = usePurchaseOrders();
  const navigate = useNavigate();

  const [poNo, setPoNo] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [showVendorWarning, setShowVendorWarning] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [deliveryType, setDeliveryType] = useState("organization");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [formData, setFormData] = useState({
    referenceNo: "",
    date: new Date().toISOString().split("T")[0],
    deliveryDate: "",
    terms: "Due on Receipt",
    shipmentPreference: "",
    items: [
      {
        id: Date.now(),
        description: "",
        qty: 1,
        rate: 0,
        amount: 0,
      },
    ],
    discountType: "%",
    discountValue: 0,
    tdsTcs: false,
    tdsTcsPercent: 0,
    adjustment: 0,
    customerNotes: "Thank you for your business.",
    termsConditions: "",
  });

  // FETCH NEXT PO NUMBER
  useEffect(() => {
    const fetchNextPoNo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchaseorders/next-po-no`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success && data.nextPoNo) {
          setPoNo(data.nextPoNo);
        } else {
          setPoNo("PO-000001");
        }
      } catch (err) {
        console.error("Failed to fetch next PO number:", err);
        setPoNo("PO-000001");
      }
    };
    fetchNextPoNo();
  }, []);

  // Load Vendors
  useEffect(() => {
    const fetchVendors = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;
      try {
        setLoadingVendors(true);
        const res = await fetch(`${API_URL}/api/vendors/get?userId=${userId}`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) =>
            getVendorDisplayName(a).localeCompare(getVendorDisplayName(b))
          );
          setVendors(sorted);
        }
      } catch (err) {
        console.error("Failed to load vendors:", err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  // Load Customers
  useEffect(() => {
    const fetchCustomers = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;
      try {
        setLoadingCustomers(true);
        const res = await fetch(
          `${API_URL}/api/customers/get?userId=${userId}`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) =>
            getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
          );
          setCustomers(sorted);
        }
      } catch (err) {
        console.error("Failed to load customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const getVendorDisplayName = (vend) => {
    const name = [vend.firstName, vend.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      name || vend.displayName || vend.companyName || "Unnamed Vendor";
    const secondary =
      vend.companyName && vend.companyName !== primary
        ? ` (${vend.companyName})`
        : "";
    return primary + secondary;
  };

  const getCustomerDisplayName = (cust) => {
    const name = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      name || cust.displayName || cust.companyName || "Unnamed Customer";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // Item handlers
  const updateItem = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "qty" || field === "rate") {
            updated.amount =
              Number(updated.qty || 0) * Number(updated.rate || 0);
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeItem = (id) => {
    if (formData.items.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: Date.now(), description: "", qty: 1, rate: 0, amount: 0 },
      ],
    }));
  };

  // Calculations
  const subtotal = formData.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    formData.discountType === "%"
      ? (subtotal * formData.discountValue) / 100
      : formData.discountValue;
  const tdsTcsAmount = formData.tdsTcs
    ? (subtotal * formData.tdsTcsPercent) / 100
    : 0;
  const total =
    subtotal - discountAmount - tdsTcsAmount + Number(formData.adjustment || 0);

  // SAVE PURCHASE ORDER
  const handleSave = async (status = "DRAFT") => {
    if (!selectedVendor) {
      setShowVendorWarning(true);
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("Session expired. Please login again.");
      navigate("/");
      return;
    }

    const poData = {
      userId,
      poNo,
      referenceNo: formData.referenceNo,
      vendorName: selectedVendor,
      date: formData.date,
      deliveryDate: formData.deliveryDate || formData.date,
      terms: formData.terms,
      shipmentPreference: formData.shipmentPreference,
      deliveryType,
      deliveryTo:
        deliveryType === "organization" ? "Organization" : selectedCustomer,
      status,
      items: formData.items.map((i) => ({
        description: i.description,
        qty: Number(i.qty),
        rate: Number(i.rate),
        amount: Number(i.amount),
      })),
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      tdsTcs: formData.tdsTcs,
      tdsTcsPercent: formData.tdsTcs ? formData.tdsTcsPercent : 0,
      adjustment: Number(formData.adjustment || 0),
      customerNotes: formData.customerNotes,
      termsConditions: formData.termsConditions,
      subtotal,
      discountAmount,
      tdsTcsAmount,
      total,
      amount: total,
      balanceDue: total,
    };

    try {
      const res = await fetch(`${API_URL}/api/purchaseorders/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(poData),
      });

      const result = await res.json();

      if (result.success) {
        addPurchaseOrder(result.data);
        alert(status === "SENT" ? "Purchase Order Sent!" : "Saved as Draft!");
        navigate("/purchase/purchase-orders");
      } else {
        alert("Save Failed: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    }
  };

  return (
    <div className="add-po-page">
      <div className="po-header">
        <h1>New Purchase Order</h1>
      </div>

      <div className="po-form">
        {/* Vendor & PO# */}
        <div className="form-row top-row">
          <div className="form-group vendor-group">
            <label>
              Vendor Name <span className="required">*</span>
            </label>
            <select
              value={selectedVendor}
              onChange={(e) => {
                setSelectedVendor(e.target.value);
                setShowVendorWarning(false);
              }}
              disabled={loadingVendors}
            >
              <option value="">
                {loadingVendors ? "Loading..." : "Select Vendor"}
              </option>
              {vendors.map((v) => (
                <option key={v._id} value={getVendorDisplayName(v)}>
                  {getVendorDisplayName(v)}
                </option>
              ))}
            </select>
            <div className="add-vendor-link">
              <Link to="/purchase/vendors/new">+ Add New Vendor</Link>
            </div>
            {showVendorWarning && (
              <small className="warning">Please select a vendor</small>
            )}
          </div>

          <div className="form-group">
            <label>Purchase Order#</label>
            <input type="text" value={poNo} readOnly />
          </div>
        </div>

        {/* Delivery Address */}
        <div className="form-row">
          <div className="form-group full-width">
            <label>
              Delivery Address <span className="required">*</span>
            </label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="organization"
                  checked={deliveryType === "organization"}
                  onChange={(e) => setDeliveryType(e.target.value)}
                />
                Organization
              </label>
              <label>
                <input
                  type="radio"
                  value="customer"
                  checked={deliveryType === "customer"}
                  onChange={(e) => setDeliveryType(e.target.value)}
                />
                Customer
              </label>
            </div>
            {deliveryType === "customer" && (
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                disabled={loadingCustomers}
              >
                <option value="">
                  {loadingCustomers ? "Loading..." : "Select Customer"}
                </option>
                {customers.map((c) => (
                  <option key={c._id} value={getCustomerDisplayName(c)}>
                    {getCustomerDisplayName(c)}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Reference, Date, etc. */}
        <div className="form-row">
          <div className="form-group">
            <label>Reference#</label>
            <input
              type="text"
              value={formData.referenceNo}
              onChange={(e) =>
                setFormData({ ...formData, referenceNo: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>PO Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Delivery Date</label>
            <input
              type="date"
              value={formData.deliveryDate}
              onChange={(e) =>
                setFormData({ ...formData, deliveryDate: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Payment Terms</label>
            <select
              value={formData.terms}
              onChange={(e) =>
                setFormData({ ...formData, terms: e.target.value })
              }
            >
              <option>Due on Receipt</option>
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Net 60</option>
            </select>
          </div>
        </div>

        {/* Item Table */}
        <div className="item-section">
          <h3>Item Table</h3>
          <table className="item-table">
            <thead>
              <tr>
                <th>ITEM DETAILS</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>AMOUNT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="text"
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(item.id, "description", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(item.id, "qty", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(item.id, "rate", Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td>₹{item.amount.toFixed(2)}</td>
                  <td>
                    {formData.items.length > 1 && (
                      <button
                        className="delete-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addItemRow} className="add-row-btn">
            + Add New Row
          </button>
        </div>

        {/* BEAUTIFUL CALCULATION SECTION — SAME AS BILLS PAGE */}
        <div className="calculations">
          <div className="calc-left">
            <div>Sub Total</div>

            <div className="calc-row">
              Discount
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({ ...formData, discountType: e.target.value })
                }
              >
                <option value="%">%</option>
                <option value="₹">₹</option>
              </select>
              <input
                type="number"
                min="0"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: Number(e.target.value) || 0,
                  })
                }
                style={{ width: 80 }}
              />
            </div>

            <div className="calc-row">
              <label>
                <input
                  type="checkbox"
                  checked={formData.tdsTcs}
                  onChange={(e) =>
                    setFormData({ ...formData, tdsTcs: e.target.checked })
                  }
                />
                TDS/TCS
              </label>
              {formData.tdsTcs && (
                <>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.tdsTcsPercent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tdsTcsPercent: Number(e.target.value) || 0,
                      })
                    }
                    style={{ width: 70 }}
                  />
                  <span>%</span>
                </>
              )}
            </div>

            <div className="calc-row">
              Adjustment
              <input
                type="number"
                value={formData.adjustment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adjustment: Number(e.target.value) || 0,
                  })
                }
                style={{ width: 100 }}
              />
            </div>

            <div className="total">Total (₹)</div>
          </div>

          <div className="calc-right">
            <div>₹{subtotal.toFixed(2)}</div>
            <div>-₹{discountAmount.toFixed(2)}</div>
            {formData.tdsTcs && <div>-₹{tdsTcsAmount.toFixed(2)}</div>}
            <div>₹{(formData.adjustment || 0).toFixed(2)}</div>
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-bar">
          <div className="left-actions">
            <button onClick={() => handleSave("DRAFT")} className="draft-btn">
              Save as Draft
            </button>
            <button onClick={() => handleSave("SENT")} className="send-btn">
              Save and Send
            </button>
            <button
              onClick={() => navigate("/purchase/purchase-orders")}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPurchaseOrders;
